import React, { useState } from 'react';
import { connect } from 'react-redux';

import { apiBaseUrl } from '../../util/api';
import { isScrollingDisabled, selectMarketplaceStats } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, NamedLink, PartnerCarousel } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './PartnerPage.module.css';

const IMG = '/static/images/partner';
const SELL_IMG = '/static/images/sell';
const ARROW = '/static/images/landing/hero-cta-arrow.svg';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Content (copy is taken verbatim from the design) ----------

const STATS = [
  { number: '200+', label: 'buyers searching for gear daily' },
  // Live active-listings count from the shared marketplace stats (ui duck).
  { statsKey: 'items', suffix: '+', label: 'active listings' },
  { number: '0', label: 'extra payment provider fees' },
  { number: '4 min', label: 'to create a listing' },
];

const BENEFITS = [
  {
    icon: 'icon-percent.svg',
    title: 'Special partner rates',
    body: 'Even better rates. You pay no more than your card terminal would cost you anyways.',
  },
  {
    icon: 'icon-dollar.svg',
    title: 'Direct payouts, no extra fees',
    body:
      'Payments on Avento are paid out directly to your bank account. International processing included, no separate provider to set up.',
  },
  {
    icon: 'icon-check.svg',
    title: 'Verified Rigger badge',
    body:
      'A trust label on your listings so buyers recognise that we verified you as an expert rigger or trader.',
  },
  {
    icon: 'icon-trending-up.svg',
    title: 'Best visibility',
    body:
      'Your listings show in Google Search, Google Shopping, our channels, and our paid ads. No web shop to promote, we do it all for you!',
  },
  {
    icon: 'icon-upload.svg',
    title: 'Import your listings',
    body:
      'Already have an online shop? We import your listings on Avento with all important details so you don\'t have to lift a finger.',
  },
  {
    icon: 'icon-bar-chart.svg',
    title: 'Unique insights and data',
    body:
      'You get special insights on what people are searching for and how your listings perform so you can make informed decisions.',
  },
];

const REQUIREMENTS = [
  'You are a rigger or professional trader who actively sells used skydiving gear.',
  'You offer expertise and high quality service, so we can stand behind your verified badge.',
  'You are open to feedback and actively listing your used gear on Avento.',
];

const TYPE_OPTIONS = ['Rigger', 'Gearshop', 'Dropzone', 'Dealer', 'Other'];

// Same "list, sell, get paid" steps as the sell page (images live in /static/images/sell).
const STEPS = [
  {
    n: 1,
    img: 'step-1',
    title: 'Create a listing',
    body: [
      'Add details, upload photos and set a price with our seller resources.',
      'Creating a listing is free, only once you sell we keep a small fee of 3%.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-02' } },
  },
  {
    n: 2,
    img: 'step-2',
    title: 'Sell easily',
    body: [
      'Buyers can make offers or buy directly.',
      'Most items successfully sold on Avento received offers or have been bought directly within 3 weeks.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-02' } },
  },
  {
    n: 3,
    img: 'step-3',
    title: 'Ship your gear',
    body: [
      'When someone purchased your item you send it with an established carrier of your choice.',
      'You set the shipping cost in advance.',
    ],
    link: { name: 'TransactionProcessSellerPage' },
  },
  {
    n: 4,
    img: 'step-4',
    title: 'Get your money',
    body: [
      'Once your item has arrived and the buyer has approved it, you get paid directly to your bank account, usually within 2-3 days.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-08' } },
  },
];

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'WebPage',
    name: 'Become a Partner Seller on Avento',
    url: `${rootURL}/partner`,
    description:
      'Become a verified partner seller on Avento. Reach thousands of buyers across the world with special partner rates, direct payouts, listing imports and a verified rigger badge.',
  };
}

function buildFaqSchema() {
  return {
    '@type': 'FAQPage',
    mainEntity: PARTNER_FAQ.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.text },
    })),
  };
}

// ---------- Step card (plain variant: same content as the sell page, no grey card) ----------

const StepCard = ({ step }) => (
  <div className={css.stepCard}>
    <div className={css.stepImageWrap}>
      <picture>
        <source media="(min-width: 860px)" srcSet={`${SELL_IMG}/${step.img}-desktop.webp`} width={500} height={382} />
        <img
          src={`${SELL_IMG}/${step.img}-mobile.webp`}
          alt=""
          className={css.stepImage}
          width={700}
          height={312}
          loading="lazy"
        />
      </picture>
      <span className={css.stepNumber} aria-hidden="true">
        {step.n}.
      </span>
    </div>
    <div className={css.stepBody}>
      <h3 className={css.stepTitle}>{step.title}</h3>
      {step.body.map((p, i) => (
        <p key={i} className={css.stepText}>
          {p}
        </p>
      ))}
      <NamedLink className={css.learnMore} name={step.link.name} to={step.link.to}>
        Learn more
      </NamedLink>
    </div>
  </div>
);

// ---------- FAQ accordion ----------

const AccordionItem = ({ id, title, open, onToggle, children }) => (
  <div className={css.accordionCard}>
    <button
      type="button"
      className={css.accordionTrigger}
      onClick={() => onToggle(id)}
      aria-expanded={open}
      aria-controls={`panel-${id}`}
      id={`trigger-${id}`}
    >
      <h3 className={css.accordionTitle}>{title}</h3>
      <span className={`${css.toggle} ${open ? css.toggleOpen : ''}`} aria-hidden="true" />
    </button>
    <div
      className={`${css.accordionPanelWrap} ${open ? css.accordionPanelOpen : ''}`}
      id={`panel-${id}`}
      role="region"
      aria-labelledby={`trigger-${id}`}
    >
      <div className={css.accordionPanel}>{children}</div>
    </div>
  </div>
);

// `text` mirrors each answer as plain text for the FAQ structured data (the `a`
// field is JSX so it can carry the inline links shown on the page).
const PARTNER_FAQ = [
  {
    id: 'cost',
    q: 'What does it cost?',
    text:
      'Listing is always free. You only pay a small commission when you make a sale, and as a partner you receive a special rate that is even lower than standard fees. You will pay no more than a card terminal or online payment integration would cost you anyways.',
    a: (
      <p className={css.panelText}>
        Listing is always free. You only pay a small commission when you make a sale, and as a
        partner you receive a special rate that is even lower than{' '}
        <a href="/fees">standard fees</a>. You will pay no more than a card terminal or online
        payment integration would cost you anyways.
      </p>
    ),
  },
  {
    id: 'paid',
    q: 'How and when do I get paid?',
    text:
      "The buyer pays before you ship and the money is safely held in our escrow accounts. Once they receive the gear, they have a maximum of 14 days time to bring it to their rigger for inspection. As soon as they confirm that the gear is according to the information provided, your payout is released directly to your bank account and usually arrives within 1-2 days. No need to worry about having to take gear back, returns are only possible if the item is not according to what was described. As long as you do not provide false information, you don't have to accept returns.",
    a: (
      <>
        <p className={css.panelText}>
          The buyer pays before you ship and the money is safely held in our escrow accounts. Once
          they receive the gear, they have a maximum of 14 days time to bring it to their rigger for
          inspection. As soon as they confirm that the gear is according to the information provided,
          your payout is released directly to your bank account and usually arrives within 1-2 days.
        </p>
        <p className={css.panelText}>
          No need to worry about having to take gear back, returns are only possible if the item is
          not according to what was described. As long as you do not provide false information, you
          don't have to accept returns.
        </p>
      </>
    ),
  },
  {
    id: 'import',
    q: 'Can I import the listings of my existing online shop?',
    text:
      'Yes, we bring your listings across for you to make it even easier. The only things we need is that you set up your payout information as we can of course not do this for you and that the listings have the necessary details including a picture of the label for main canopies and containers.',
    a: (
      <p className={css.panelText}>
        Yes, we bring your listings across for you to make it even easier. The only things we need is
        that you set up your payout information as we can of course not do this for you and that the
        listings have the necessary details including a picture of the label for main canopies and
        containers.
      </p>
    ),
  },
  {
    id: 'shipping',
    q: 'Who handles shipping?',
    text:
      'You ship to the buyer once the sale is confirmed and the payment is already secured. That way you are protected at all times. You are free to chose the carrier as long as it is an established company and tracking is provided. You can set the shipping cost to different regions when creating a listing.',
    a: (
      <p className={css.panelText}>
        You ship to the buyer once the sale is confirmed and the payment is already secured. That way
        you are protected at all times. You are free to chose the carrier as long as it is an
        established company and tracking is provided. You can set the shipping cost to different
        regions when creating a listing.
      </p>
    ),
  },
  {
    id: 'badge',
    q: 'What is the verified badge?',
    text:
      'A trust mark shown on your listing that tells buyers you are a verified rigger or professional trader with high levels of professionalism and expertise.',
    a: (
      <p className={css.panelText}>
        A trust mark shown on your listing that tells buyers you are a verified rigger or professional
        trader with high levels of professionalism and expertise.
      </p>
    ),
  },
];

// ---------- Application form ----------

const INITIAL_FORM = {
  fullName: '',
  businessName: '',
  websiteUrl: '',
  location: '',
  type: '',
  salesVolume: '',
  phone: '',
  email: '',
  message: '',
  website: '', // honeypot
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PartnerForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);

  const set = key => e => {
    const { value } = e.target;
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear the email error as soon as the user edits it.
    if (key === 'email' && emailInvalid) {
      setEmailInvalid(false);
      if (status === 'error') setStatus('idle');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.website) return;

    // Client-side validation (mirrors the server) for clear, field-level feedback.
    if (!form.fullName.trim()) {
      setEmailInvalid(false);
      setErrorMsg('Please enter your name.');
      setStatus('error');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setEmailInvalid(true);
      setErrorMsg('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setEmailInvalid(false);
    setErrorMsg('');
    setStatus('submitting');
    try {
      // Send every field except the honeypot.
      const { website, ...payload } = form;
      const res = await fetch(`${apiBaseUrl()}/api/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
    } catch (err) {
      setErrorMsg('Something went wrong. Please try again or contact us directly.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className={css.formCard}>
        <div className={css.formSuccess}>
          <h3 className={css.formSuccessHeading}>Thank you!</h3>
          <p className={css.formSuccessText}>
            Your application has been submitted. We will get back to you shortly!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={css.formCard}>
      <form className={css.form} onSubmit={handleSubmit} noValidate>
        <h3 className={css.formHeading}>Apply in 5 minutes</h3>

        <div className={css.formGrid}>
          <div className={css.formField}>
            <label htmlFor="p-fullName" className={css.formLabel}>
              Full Name
            </label>
            <input
              id="p-fullName"
              type="text"
              maxLength={256}
              className={css.input}
              placeholder="Your name"
              value={form.fullName}
              onChange={set('fullName')}
              autoComplete="name"
              required
            />
          </div>
          <div className={css.formField}>
            <label htmlFor="p-businessName" className={css.formLabel}>
              Business Name
            </label>
            <input
              id="p-businessName"
              type="text"
              maxLength={256}
              className={css.input}
              placeholder="Business name"
              value={form.businessName}
              onChange={set('businessName')}
              autoComplete="organization"
            />
          </div>
          <div className={css.formField}>
            <label htmlFor="p-websiteUrl" className={css.formLabel}>
              Website URL
            </label>
            <input
              id="p-websiteUrl"
              type="text"
              maxLength={256}
              className={css.input}
              placeholder="www.yourbusiness.com"
              value={form.websiteUrl}
              onChange={set('websiteUrl')}
              autoComplete="url"
            />
          </div>

          <div className={css.formField}>
            <label htmlFor="p-location" className={css.formLabel}>
              Location
            </label>
            <input
              id="p-location"
              type="text"
              maxLength={256}
              className={css.input}
              placeholder="City, Country"
              value={form.location}
              onChange={set('location')}
            />
          </div>
          <div className={css.formField}>
            <label htmlFor="p-type" className={css.formLabel}>
              Type
            </label>
            <select
              id="p-type"
              className={`${css.input} ${css.select} ${form.type ? '' : css.selectPlaceholder}`}
              value={form.type}
              onChange={set('type')}
            >
              <option value="" disabled>
                Select
              </option>
              {TYPE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className={css.formField}>
            <label htmlFor="p-salesVolume" className={css.formLabel}>
              How much do you sell?
            </label>
            <input
              id="p-salesVolume"
              type="text"
              maxLength={256}
              className={css.input}
              placeholder="e.g. 3-6 items per month"
              value={form.salesVolume}
              onChange={set('salesVolume')}
            />
          </div>

          <div className={css.formField}>
            <label htmlFor="p-phone" className={css.formLabel}>
              Phone
            </label>
            <input
              id="p-phone"
              type="tel"
              maxLength={50}
              className={css.input}
              placeholder="+00 00 000 00 00"
              value={form.phone}
              onChange={set('phone')}
              autoComplete="tel"
            />
          </div>
          <div className={`${css.formField} ${css.formFieldWide}`}>
            <label htmlFor="p-email" className={css.formLabel}>
              Email
            </label>
            <input
              id="p-email"
              type="email"
              maxLength={256}
              className={`${css.input} ${emailInvalid ? css.inputError : ''}`}
              placeholder="you@email.com"
              value={form.email}
              onChange={set('email')}
              autoComplete="email"
              aria-invalid={emailInvalid}
              required
            />
          </div>
        </div>

        <div className={css.formField}>
          <label htmlFor="p-message" className={css.formLabel}>
            Anything you want to tell us?
          </label>
          <textarea
            id="p-message"
            maxLength={5000}
            className={`${css.input} ${css.textarea}`}
            placeholder="your message"
            value={form.message}
            onChange={set('message')}
          />
        </div>

        {/* Honeypot */}
        <div className={css.honeypot} aria-hidden="true">
          <label htmlFor="p-website">Website</label>
          <input
            id="p-website"
            type="text"
            name="website"
            value={form.website}
            onChange={set('website')}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div className={css.formActions}>
          <button type="submit" className={css.submitButton} disabled={status === 'submitting'}>
            {status === 'submitting' ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {status === 'error' && (
          <p className={css.formError} role="alert">
            {errorMsg || 'Something went wrong. Please try again or contact us directly.'}
          </p>
        )}
      </form>
    </div>
  );
};

// ---------- Main component ----------

const PartnerPageComponent = props => {
  const { scrollingDisabled, marketplaceStats } = props;
  const config = useConfiguration();
  const rootURL = config.marketplaceRootURL || '';

  const [openFaq, setOpenFaq] = useState({});
  const toggleFaq = id => setOpenFaq(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <Page
      title="Become a Partner Seller on Avento"
      description="Become a verified partner seller on Avento. Reach thousands of buyers worldwide with special partner rates, direct payouts, listing imports and a verified rigger badge."
      schema={[buildPageSchema(rootURL), buildFaqSchema()]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="PartnerPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Hero — dynamic title overlaid on a text-free image (home-hero technique). */}
                <section className={css.heroSection}>
                  <picture>
                    <source
                      media="(min-width: 860px)"
                      srcSet={`${IMG}/partner-hero-desktop.webp`}
                      width={1900}
                      height={440}
                    />
                    <img
                      src={`${IMG}/partner-hero-mobile.webp`}
                      alt=""
                      className={css.heroImage}
                      width={900}
                      height={900}
                      fetchpriority="high"
                    />
                  </picture>
                  <div className={css.heroOverlay}>
                    <h1 className={css.heroTitle}>
                      <span>Become a partner</span>
                      <span>seller</span>
                    </h1>
                    <p className={css.heroSubtitle}>
                      Reach <strong>thousands</strong> of buyers across the world
                    </p>
                    <a href="#partner-form" className={css.heroApply}>
                      <span>APPLY</span>
                      <img src={`${IMG}/icon-arrow-right.svg`} alt="" className={css.heroApplyArrow} width={20} height={20} />
                    </a>
                  </div>
                </section>

                {/* Stats */}
                <section className={css.statsSection}>
                  <div className={css.container}>
                    <div className={css.statsGrid}>
                      {STATS.map(stat => {
                        const liveValue = stat.statsKey
                          ? marketplaceStats?.[stat.statsKey]
                          : null;
                        // Live-number stats render only with real data, never placeholders.
                        if (stat.statsKey && liveValue == null) return null;
                        const number = stat.statsKey ? `${liveValue}${stat.suffix}` : stat.number;
                        return (
                          <div key={stat.label} className={css.statCard}>
                            <span className={css.statNumber}>{number}</span>
                            <span className={css.statLabel}>{stat.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Avento benefits */}
                <section className={css.benefitsSection}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>Avento benefits</h2>
                    <div className={css.benefitsGrid}>
                      {BENEFITS.map(benefit => (
                        <div key={benefit.title} className={css.benefitCard}>
                          <div className={css.benefitHead}>
                            <img src={`${IMG}/${benefit.icon}`} alt="" className={css.benefitIcon} width={22} height={22} />
                            <h3 className={css.benefitTitle}>{benefit.title}</h3>
                          </div>
                          <p className={css.benefitText}>{benefit.body}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Trusted by — partner logo carousel (same behaviour as the home page) */}
                <section className={css.trustedSection}>
                  <div className={css.container}>
                    <h2 className={css.trustedHeading}>
                      Trusted by riggers and gear shops across Europe
                    </h2>
                  </div>
                  <div className={css.trustedStrip}>
                    <PartnerCarousel />
                  </div>
                </section>

                {/* List, sell, get paid (same steps as the sell page, plain cards) */}
                <section className={css.stepsSection}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>List, sell, get paid</h2>
                    <div className={css.stepsGrid}>
                      {STEPS.map(step => (
                        <StepCard key={step.n} step={step} />
                      ))}
                    </div>
                  </div>
                </section>

                {/* Partner requirements */}
                <section className={css.requirementsSection}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>Partner requirements</h2>
                    <div className={css.requirementsGrid}>
                      <p className={css.requirementsText}>
                        We partner with professionals that have high levels of expertise and
                        commitment so our buyers know that they can trust you and the partner badge
                        stays meaningful.
                      </p>
                      <ol className={css.requirementsList}>
                        {REQUIREMENTS.map((req, i) => (
                          <li key={i} className={css.requirementItem}>
                            <span className={css.requirementNumber} aria-hidden="true">
                              {i + 1}.
                            </span>
                            <span className={css.requirementBody}>{req}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </section>

                {/* Become a partner — application form */}
                <section className={css.formSection} id="partner-form">
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>Become a partner</h2>
                    <PartnerForm />
                  </div>
                </section>

                {/* FAQ */}
                <section className={css.faqSection}>
                  <div className={css.container}>
                    <h2 className={`${css.sectionHeading} ${css.faqHeading}`}>FAQ</h2>
                    <p className={css.faqIntro}>
                      If you have any other questions, please feel free to shoot us a message. We are
                      happy to hear from you!
                    </p>
                    <NamedLink name="ContactPage" className={css.contactButton}>
                      Contact
                    </NamedLink>
                    <div className={css.accordion}>
                      {PARTNER_FAQ.map(item => (
                        <AccordionItem
                          key={item.id}
                          id={item.id}
                          title={item.q}
                          open={!!openFaq[item.id]}
                          onToggle={toggleFaq}
                        >
                          {item.a}
                        </AccordionItem>
                      ))}
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  marketplaceStats: selectMarketplaceStats(state),
});

const PartnerPage = connect(mapStateToProps)(PartnerPageComponent);

export default PartnerPage;
